import { makeStyles } from '@mui/styles';
import palette from '../../../../theme/palette';
import { DROPDOWN_CONFIG, STYLE_CONSTANTS } from '../../constants';

const CELL_FONT_SIZE = '13px';
const LABEL_FONT_SIZE = '12px';
const SMALL_LABEL_FONT_SIZE = '11px';
const BUTTON_FONT_SIZE = '12px';
const BORDER_COLOR = palette.grey[100];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  breadcrumbContainer: {
    marginBottom: theme.spacing(3),
  },
  pageTitle: {
    marginBottom: theme.spacing(3),
  },
  headerPanel: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
  headerDisclaimer: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(2),
    color: theme.palette.grey[800],
    fontSize: '11px',
    fontStyle: 'italic',
    display: 'flex',
    alignItems: 'center',
    zIndex: 1,
  },
  successAlert: {
    marginBottom: theme.spacing(2),
  },
  successActions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  postApprovalActionButton: {
    minHeight: STYLE_CONSTANTS.APPROVAL_BUTTON_MIN_HEIGHT,
  },
  postApprovalActionsCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  fieldLabel: {
    marginBottom: theme.spacing(0.5),
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: LABEL_FONT_SIZE,
    textTransform: 'uppercase',
  },
  currencyValueWrap: {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    minHeight: 24,
    width: '100%',
    margin: 0,
    padding: 0,
    paddingRight: theme.spacing(3),
    overflowX: 'hidden',
    overflowY: 'hidden',
    whiteSpace: 'normal',
  },
  currencyValueChip: {
    height: 24,
    flexShrink: 0,
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.primary.lighter,
    color: theme.palette.primary.main,
    fontSize: '11px',
    fontWeight: theme.typography.fontWeightSemiBold,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.2),
    },
    '& .MuiChip-deleteIcon': {
      color: theme.palette.grey[800],
      fontSize: '13px',
      marginRight: theme.spacing(0.25),
      marginLeft: 0,
      '&:hover': {
        color: theme.palette.grey[900],
      },
    },
  },
  currencyPlaceholder: {
    color: theme.palette.text.disabled,
    fontSize: '14px',
    lineHeight: 1.4,
    fontWeight: 400,
    margin: 0,
    padding: 0,
  },
  disabledDropdown: {},
  currencyFormControl: {
    '& .MuiOutlinedInput-root': {
      padding: 0,
      minHeight: 40,
      height: 'auto',
      alignItems: 'center',
    },
    '& .MuiSelect-select.MuiSelect-multiple': {
      display: 'flex',
      alignItems: 'center',
      alignContent: 'center',
      flexWrap: 'wrap',
      minHeight: '40px !important',
      maxHeight: '56px',
      overflow: 'hidden',
      padding: theme.spacing(0.5, 4, 0.5, 1.25),
      boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(0.5, 4, 0.5, 1.25),
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      boxSizing: 'border-box',
    },
  },
  sublocationSearchContainer: {
    padding: theme.spacing(1, 2),
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.common.white,
    zIndex: 11,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  sublocationButtonContainer: {
    padding: theme.spacing(1, 2),
    display: 'flex',
    gap: theme.spacing(1),
    position: 'sticky',
    top: 56,
    backgroundColor: theme.palette.common.white,
    zIndex: 10,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  sublocationButton: {
    flex: '1 1 0',
    maxWidth: `calc(50% - ${theme.spacing(0.5)})`,
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    fontSize: BUTTON_FONT_SIZE,
  },
  sublocationMenuItem: {
    minHeight: STYLE_CONSTANTS.MENU_ITEM_MIN_HEIGHT,
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    '& .MuiListItemText-root': {
      overflow: 'hidden',
    },
    '& .MuiListItemText-primary': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  currencyMenuItem: {
    minHeight: 36,
    padding: theme.spacing(0.75, 0),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .MuiCheckbox-root': {
      padding: theme.spacing(0.5),
    },
  },
  sublocationName: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: CELL_FONT_SIZE,
  },
  sublocationNameText: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sublocationEndDate: {
    marginLeft: theme.spacing(2),
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
    fontSize: LABEL_FONT_SIZE,
    fontWeight: theme.typography.fontWeightMedium,
  },
  selectedPopoverContainer: {
    width: '100%',
    maxWidth: 420,
    alignSelf: 'flex-end',
  },
  selectedPopoverTrigger: {
    minHeight: 40,
    width: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(0.75),
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(0.5, 1.5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    cursor: 'pointer',
    outline: 'none',
    font: 'inherit',
    textAlign: 'left',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[50],
    },
    '&:focus-visible': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.light}`,
    },
  },
  selectedPopoverTriggerTitle: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightSemiBold,
    fontSize: CELL_FONT_SIZE,
    lineHeight: 1.3,
  },
  selectedPopoverTriggerHint: {
    color: theme.palette.text.secondary,
    fontSize: SMALL_LABEL_FONT_SIZE,
    lineHeight: 1.3,
  },
  selectedPopoverPaper: {
    maxHeight: STYLE_CONSTANTS.POPOVER_MAX_HEIGHT,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: theme.customShadows.popover,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  selectedPopoverList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
  },
  selectedPopoverHeader: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  selectedPopoverHeaderTitle: {
    fontWeight: theme.typography.fontWeightSemiBold,
    color: theme.palette.text.primary,
    fontSize: '14px',
  },
  selectedPopoverHeaderCaption: {
    color: theme.palette.text.secondary,
    fontSize: LABEL_FONT_SIZE,
  },
  selectedPopoverRow: {
    padding: theme.spacing(1.25, 2),
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  selectedPopoverRowMain: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    gap: theme.spacing(1),
  },
  selectedPopoverRowContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    gap: theme.spacing(1.5),
  },
  selectedPopoverRowName: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.text.primary,
    fontSize: CELL_FONT_SIZE,
  },
  selectedPopoverRowNameText: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  selectedPopoverRowDate: {
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: LABEL_FONT_SIZE,
  },
  selectedPopoverRemoveButton: {
    flexShrink: 0,
    marginTop: -1,
    color: theme.palette.grey[500],
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedPopoverAddButton: {
    flexShrink: 0,
    marginTop: -1,
    color: theme.palette.grey[500],
    '&:hover': {
      color: theme.palette.success.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  applyButton: {
    padding: theme.spacing(1, 4),
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  dateConstraintHintPanel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.spacing(0.75),
    backgroundColor: theme.palette.warning.lighter,
    border: `1px solid ${theme.palette.warning.light}`,
  },
  dateConstraintHintIcon: {
    color: theme.palette.warning.dark,
    fontSize: '20px',
    marginTop: 1,
    flexShrink: 0,
  },
  dateConstraintHintText: {
    color: theme.palette.warning.dark,
    fontSize: '14px',
    lineHeight: 1.5,
    fontWeight: theme.typography.fontWeightMedium,
  },
  addRowButton: {
    padding: theme.spacing(1.25, 3),
  },
  approveButton: {
    padding: theme.spacing(1.25, 6.375),
  },
  actionButtonsCard: {
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
  },
  actionInstructionPanel: {
    padding: theme.spacing(0.5, 0),
    maxWidth: 760,
  },
  actionInstructionTitle: {
    fontSize: '20px',
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  actionInstructionList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  actionInstructionChecklist: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
  },
  actionInstructionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  actionInstructionContent: {
    minWidth: 0,
  },
  actionInstructionDoneIcon: {
    color: theme.palette.success.main,
    fontSize: '20px',
    marginTop: 2,
    flexShrink: 0,
  },
  actionInstructionStepNumber: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.grey[400]}`,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.common.white,
    fontSize: LABEL_FONT_SIZE,
    fontWeight: theme.typography.fontWeightSemiBold,
    lineHeight: '20px',
    textAlign: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  actionInstructionStepNumberCurrent: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.lighter,
  },
  actionInstructionItem: {
    fontSize: '15px',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.25),
    lineHeight: 1.5,
  },
  actionInstructionNote: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  feedSourceEmptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  tableFooterText: {
    padding: theme.spacing(1, 2, 2),
    fontSize: LABEL_FONT_SIZE,
    color: theme.palette.secondary.main,
  },
  cellText: {
    fontSize: CELL_FONT_SIZE,
  },
  cellTextBold: {
    fontSize: CELL_FONT_SIZE,
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  feedSourceContainer: {
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.grey[100]}`,
    // Lock table layout to prevent column recalculation when rows expand/collapse
    '& table': {
      tableLayout: 'fixed',
      width: '100%',
    },
    // Feed Source column — 20% width to match original layout proportions
    '& th:first-child, & td:first-child': {
      width: '20% !important',
    },
  },
  feedSourceCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    overflow: 'hidden',
  },
  expandToggleBtn: {
    padding: 2,
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
  expandIconSlot: {
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  expandIcon: {
    fontSize: '18px !important',
  },
  currencySubRowLabel: {
    paddingLeft: theme.spacing(4),
    overflow: 'hidden',
    minWidth: 0,
    fontSize: CELL_FONT_SIZE,
    color: theme.palette.secondary.main,
    '& .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
  },
  currencySubRowCell: {
    borderTop: 'none !important',
    transition: 'none !important',
    animation: 'none !important',
    transform: 'none !important',
    '& .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
  },
  expandedParentRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none !important',
    },
  },
  currencySubRow: {
    '& .MuiTableCell-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
    '& .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
    '&:hover .MuiTableCell-root, &:hover .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
  },
  currencySubRowNoDivider: {
    '& .MuiTableCell-root': {
      borderBottom: 'none !important',
    },
  },
  sublocationSubRowLabel: {
    // Keep sublocation label aligned with currency breakdown labels (USD/CAD).
    paddingLeft: theme.spacing(4),
    overflow: 'hidden',
    minWidth: 0,
    fontSize: CELL_FONT_SIZE,
    color: theme.palette.secondary.main,
    '& .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
  },
  sublocationSubRowCell: {
    borderTop: 'none !important',
    transition: 'none !important',
    animation: 'none !important',
    transform: 'none !important',
    '& .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
  },
  sublocationSubRow: {
    '& .MuiTableCell-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
    '& .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
    '&:hover .MuiTableCell-root, &:hover .MuiTypography-root': {
      color: `${theme.palette.secondary.main} !important`,
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
  },
  sublocationSubRowNoDivider: {
    '& .MuiTableCell-root': {
      borderBottom: 'none !important',
    },
  },
  sublocationSectionHeaderRow: {
    '& .MuiTableCell-root': {
      // Match shared ReactTable header row style exactly.
      backgroundColor: theme.palette.grey[900_2],
      borderTop: `1px solid ${theme.palette.grey[900_2]}`,
      borderBottom: `1px solid ${theme.palette.grey[900_2]}`,
      color: `${theme.palette.secondary.main} !important`,
    },
  },
  sublocationSectionHeaderCell: {
    borderTop: 'none !important',
  },
  sublocationSectionHeaderText: {
    width: '100%',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1.4,
    color: `${theme.palette.secondary.main} !important`,
    textTransform: 'none',
    letterSpacing: 0,
  },
  sublocationMoreRow: {
    '& .MuiTableCell-root': {
      color: `${theme.palette.secondary.main} !important`,
      borderTop: 'none !important',
      transition: 'none !important',
      animation: 'none !important',
      transform: 'none !important',
    },
  },
  sublocationMoreRowCell: {
    borderTop: 'none !important',
  },
  sublocationMoreLabel: {
    fontSize: CELL_FONT_SIZE,
    fontWeight: theme.typography.fontWeightMedium,
    color: `${theme.palette.secondary.main} !important`,
    cursor: 'pointer',
    display: 'inline-block',
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:focus-visible': {
      outline: `1px dashed ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  glWarningPanel: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.lighter,
    border: `1px solid ${theme.palette.warning.light}`,
    maxHeight: STYLE_CONSTANTS.WARNING_PANEL_MAX_HEIGHT,
    overflowY: 'auto',
  },
  glWarningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  glWarningHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    lineHeight: 1,
  },
  glWarningHeaderTitle: {
    lineHeight: 1.2,
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: 1,
  },
  glWarningIcon: {
    color: theme.palette.warning.dark,
    fontSize: '20px',
    display: 'block',
    flexShrink: 0,
    transform: 'translateY(-1px)',
  },
  glWarningText: {
    fontSize: CELL_FONT_SIZE,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  groupContainer: {
    border: '2px solid',
    borderColor: 'transparent',
    borderRadius: theme.spacing(1),
    margin: theme.spacing(1, 1, 1.5, 1),
    overflow: 'hidden',
  },
  groupHeader: {
    padding: theme.spacing(1, 2),
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  groupTitleRow: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(0.25),
    '&:hover': {
      '& $groupExpandIcon': {
        backgroundColor: theme.palette.grey[200],
      },
    },
  },
  groupExpandIcon: {
    padding: theme.spacing(0.25),
    borderRadius: '4px',
    transition: 'background-color 0.2s',
  },
  groupLabel: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: LABEL_FONT_SIZE,
    marginLeft: theme.spacing(0.5),
  },
  groupActionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },
  groupActionButton: {
    fontSize: '11px',
    padding: theme.spacing(0.25, 1),
    minWidth: 'auto',
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
  },
  showMoreButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 2),
    borderTop: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.08)',
  },
  showMoreButton: {
    fontSize: '12px',
    padding: theme.spacing(0.5, 2),
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
}));

export const getSublocationMenuProps = (theme) => ({
  PaperProps: {
    style: {
      maxHeight: DROPDOWN_CONFIG.SUBLOCATION_MENU_MAX_HEIGHT,
      width: DROPDOWN_CONFIG.SUBLOCATION_MENU_WIDTH,
      maxWidth: 'calc(100vw - 32px)',
      border: `1px solid ${palette.grey[300]}`,
      borderRadius: STYLE_CONSTANTS.SUBLOCATION_MENU_BORDER_RADIUS,
      boxShadow: theme.customShadows.dropdown,
    },
  },
  autoFocus: false,
});

export const getFeedSourceTableStyles = () => ({
  borderCollapse: 'collapse',
  width: '100%',
  '& .MuiTableHead-root': {
    '& .MuiTableRow-root': {
      '& .MuiTableCell-root': {
        border: `1px solid ${palette.common.black}`,
        borderRight: `1px solid ${BORDER_COLOR}`,
        borderLeft: `1px solid ${BORDER_COLOR}`,
        backgroundColor: palette.grey[200],
        padding: '12px',
        fontSize: CELL_FONT_SIZE,
        fontWeight: 600,
        textAlign: 'left',
      },
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      height: '48px',
      '& .MuiTableCell-root': {
        border: `1px solid ${BORDER_COLOR}`,
        borderRight: `1px solid ${BORDER_COLOR}`,
        borderLeft: `1px solid ${BORDER_COLOR}`,
        fontSize: CELL_FONT_SIZE,
      },
      '& .MuiTableCell-root:nth-of-type(2), & .MuiTableCell-root:nth-of-type(3), & .MuiTableCell-root:nth-of-type(4), & .MuiTableCell-root:nth-of-type(5)':
        {
          textAlign: 'right',
        },
    },
    '& .MuiTableRow-root:last-child': {
      backgroundColor: palette.grey[700],
      '& .MuiTableCell-root': {
        borderTop: `2px solid ${BORDER_COLOR}`,
      },
    },
  },
});

export default useStyles;
